import moment, { Moment } from 'moment'
import qs from 'qs'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  DatesInputTrigger,
  GuestsInputTrigger,
  PlaceInputTrigger,
} from './Common/SearchFields'
import SearchAirports from './SearchAirports'
import SearchDatePicker from './SearchDatePicker'
import SearchOccupancyPicker from './SearchOccupancyPicker'
import useDefaultDates from '../Common/Hooks/useDefaultDates'

import { addToTripClickEvent } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FareDropdown from 'components/Flights/FlightsSearch/SearchWidget/FareDropdown/FareDropdown'
import { FlightDropdownOption } from 'components/Flights/types'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import TextButton from 'components/Luxkit/Button/TextButton'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import Group from 'components/utils/Group'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import {
  fareCabins,
  FareType,
  fareTypes,
  FlightsFareTypes,
} from 'constants/flight'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import useNavigateTo from 'hooks/useNavigateTo'
import {
  countOccupantsForFlights,
  isValidFlightsOccupants,
} from 'lib/offer/occupancyUtils'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import {
  FLIGHTS_SEARCH_DAY_MAX_ADVANCE,
  FLIGHTS_SEARCH_DAY_MIN_ADVANCE,
} from 'tripPlanner/config/flights'
import { useTripMetadataPrefetch, useTrip } from 'tripPlanner/hooks/api/trip'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'

interface Props {
  tripId: string
  sectionId?: string
  defaultDate?: string
  prefillData?: {
    departureAirport: App.AirportLocation | undefined
    arrivalAirport: App.AirportLocation | undefined
    fareType?: FareType
    checkinDate?: Moment
    checkoutDate?: Moment
  }
}

function SearchFlights({ tripId, sectionId, prefillData, defaultDate }: Props) {
  useTripMetadataPrefetch({ tripId })

  const { data: trip } = useTrip({ tripId })

  const { setImmersiveMode } = useImmersiveModeHandlers()
  const navigateTo = useNavigateTo()

  const openModal = useOpenModal()
  const { closeModal, goBack } = useModalCallbacks()

  const { flights, fareCabin, fareType, occupants } = useContext(
    FlightSearchWidgetStateContext,
  )

  const {
    id: flightId,
    checkinDate,
    checkoutDate,
    departureAirport,
    arrivalAirport,
    showDepartureError,
    showDestinationError,
    showSameAirportError,
    showTravelDatesError,
  } = flights[0]

  const flightsDispatch = useContext(FlightSearchWidgetDispatchContext)

  const [isAirportSelectDirty, setIsAirportSelectDirty] = useState(
    !!departureAirport && !!arrivalAirport,
  )
  const [isDatesDirty, setIsDatesDirty] = useState(
    !!checkinDate && !!checkoutDate,
  )

  // Same logic as in components\Flights\FlightsSearch\SearchWidget\DateSelectInput\DateSelectInput.tsx
  const minSearchDate = moment().add(FLIGHTS_SEARCH_DAY_MIN_ADVANCE, 'day')
  const maxSearchDate = moment().add(FLIGHTS_SEARCH_DAY_MAX_ADVANCE, 'day')

  const defaultDates = useDefaultDates({
    activeDate: defaultDate,
    tripStartDate: trip?.startDate,
    tripEndDate: trip?.endDate,
    minDate: minSearchDate,
    maxDate: maxSearchDate,
  })

  useEffect(() => {
    if (
      checkinDate ||
      checkoutDate ||
      !defaultDates.start ||
      !defaultDates.end
    ) {
      return
    }
    flightsDispatch({
      type: FlightSearchWidgetActions.SET_CHECKIN_DATE,
      id: flightId,
      date: defaultDates.start,
    })
    flightsDispatch({
      type: FlightSearchWidgetActions.SET_CHECKOUT_DATE,
      id: flightId,
      date: defaultDates.end,
    })
  }, [checkinDate, checkoutDate, flightsDispatch, defaultDates, flightId])

  const isOccupantsValid = useMemo(
    () => isValidFlightsOccupants(occupants),
    [occupants],
  )

  const canSearch =
    !showDepartureError &&
    !showDestinationError &&
    !showSameAirportError &&
    !showTravelDatesError &&
    isOccupantsValid

  const invalidDateSelection = useMemo(() => {
    if (fareType?.value === 'return') {
      return !checkinDate || !checkoutDate
    } else {
      return !checkinDate
    }
  }, [fareType, checkinDate, checkoutDate])

  const onSetDates = useCallback(
    (startDate?: Moment, endDate?: Moment) => {
      flightsDispatch({
        type: FlightSearchWidgetActions.SET_CHECKIN_DATE,
        date: startDate || null,
        id: flightId,
      })
      flightsDispatch({
        type: FlightSearchWidgetActions.SET_CHECKOUT_DATE,
        date: endDate || null,
        id: flightId,
      })
    },
    [flightsDispatch, flightId],
  )

  const onSetOccupancies = useCallback(
    (occupancies: Array<App.Occupants>) => {
      // Flights should only have 1 occupants object
      const occupants = occupancies[0]

      flightsDispatch({
        type: FlightSearchWidgetActions.SET_OCCUPANTS,
        occupants,
      })
    },
    [flightsDispatch],
  )

  useEffect(() => {
    if (!departureAirport?.airportCode) {
      flightsDispatch({
        type: FlightSearchWidgetActions.SHOW_DEPARTURE_ERROR,
        id: flightId,
        error: true,
      })
    } else {
      flightsDispatch({
        type: FlightSearchWidgetActions.SHOW_DEPARTURE_ERROR,
        id: flightId,
        error: false,
      })
    }

    if (!arrivalAirport?.airportCode) {
      flightsDispatch({
        type: FlightSearchWidgetActions.SHOW_ARRIVAL_ERROR,
        id: flightId,
        error: true,
      })
    } else {
      flightsDispatch({
        type: FlightSearchWidgetActions.SHOW_ARRIVAL_ERROR,
        id: flightId,
        error: false,
      })
    }

    if (invalidDateSelection) {
      flightsDispatch({
        type: FlightSearchWidgetActions.SHOW_TRAVEL_DATES_ERROR,
        id: flightId,
        error: true,
      })
    } else {
      flightsDispatch({
        type: FlightSearchWidgetActions.SHOW_TRAVEL_DATES_ERROR,
        id: flightId,
        error: false,
      })
    }

    if (departureAirport?.airportCode === arrivalAirport?.airportCode) {
      flightsDispatch({
        type: FlightSearchWidgetActions.SHOW_SAME_AIRPORT_ERROR,
        id: flightId,
        error: true,
      })
    } else {
      flightsDispatch({
        type: FlightSearchWidgetActions.SHOW_SAME_AIRPORT_ERROR,
        id: flightId,
        error: false,
      })
    }
  }, [
    departureAirport?.airportCode,
    arrivalAirport?.airportCode,
    invalidDateSelection,
    flightsDispatch,
    flightId,
  ])

  useEffect(() => {
    if (prefillData?.departureAirport && !departureAirport) {
      flightsDispatch({
        type: FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT,
        id: flightId,
        airport: prefillData.departureAirport,
      })
    }

    if (prefillData?.arrivalAirport && !arrivalAirport) {
      flightsDispatch({
        type: FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT,
        id: flightId,
        airport: prefillData.arrivalAirport,
      })
    }

    if (prefillData?.checkinDate && !checkinDate) {
      flightsDispatch({
        type: FlightSearchWidgetActions.SET_CHECKIN_DATE,
        id: flightId,
        date: prefillData?.checkinDate,
      })
    }

    if (prefillData?.checkoutDate && !checkoutDate) {
      flightsDispatch({
        type: FlightSearchWidgetActions.SET_CHECKOUT_DATE,
        id: flightId,
        date: prefillData.checkoutDate,
      })
    }
  }, [
    arrivalAirport,
    checkinDate,
    checkoutDate,
    departureAirport,
    flightsDispatch,
    fareType,
    prefillData,
    flightId,
  ])

  useEffect(() => {
    if (prefillData?.fareType) {
      const prefilledFareType = fareTypes.find(
        (t) => !t.disabled && t.value === prefillData.fareType,
      )
      if (prefilledFareType) {
        flightsDispatch({
          type: FlightSearchWidgetActions.SET_FARE_TYPE,
          fareType: prefilledFareType,
        })
      }
    }
  }, [flightsDispatch, prefillData?.fareType])

  const changeFareType = useCallback(
    (fareType: FlightDropdownOption) => {
      flightsDispatch({
        type: FlightSearchWidgetActions.SET_FARE_TYPE,
        fareType,
      })
    },
    [flightsDispatch],
  )

  const changeFareCabin = useCallback(
    (fareCabin: FlightDropdownOption) => {
      flightsDispatch({
        type: FlightSearchWidgetActions.SET_FARE_CABIN,
        fareCabin,
      })
    },
    [flightsDispatch],
  )
  const source = useTripClientSource()
  const onSearchAirport = () => {
    fireInteractionEvent(addToTripClickEvent('modal_flights_search'))
    const transformedOccupants = countOccupantsForFlights([occupants])
    const isReturnFare = fareType?.value === FlightsFareTypes.RETURN

    const queryParams = {
      originAirportCode: departureAirport?.airportCode,
      originAirportName: departureAirport?.airportName,
      destinationAirportCode: arrivalAirport?.airportCode,
      destinationAirportName: arrivalAirport?.airportName,
      departDate: moment(checkinDate).format(ISO_DATE_FORMAT),
      returnDate: isReturnFare ?
        moment(checkoutDate).format(ISO_DATE_FORMAT) :
        null,
      adults: transformedOccupants.adults,
      children: transformedOccupants.children,
      infants: transformedOccupants.infants,
      childrenAge: transformedOccupants.childrenAge?.join(','),
      fareClass: fareCabin?.value,
      fareType: fareType?.value,
      searchId: moment().unix(),
      source,
      // carrier: isFlightsCredit ? creditDetails?.carrier : undefined,
      // flightsCredit: isFlightsCredit || undefined,
    }

    const query = qs.stringify(queryParams)
    if (trip && !trip.isTemplate) {
      setImmersiveMode({ tripId, sectionId })
    }
    navigateTo('/flights-search-results', query)
  }

  const isMobile = useScreenSizeOnly('mobile')

  return (
    <>
      <ModalHeader
        title="Search flights"
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={16}>
            <VerticalSpacer gap={12}>
              <div>
                <FareDropdown
                  options={fareTypes}
                  onOptionChange={changeFareType}
                  selectedOption={fareType}
                />
                <FareDropdown
                  options={fareCabins}
                  selectedOption={fareCabin}
                  onOptionChange={changeFareCabin}
                />
              </div>
              <PlaceInputTrigger
                title="From"
                subTitle={departureAirport?.airportName || 'Select an airport'}
                onClick={() => {
                  setIsAirportSelectDirty(true)
                  openModal(SearchAirports, {
                    tripId,
                    airportType: 'departure' as const,
                    flightId,
                  })
                }}
              />
              <PlaceInputTrigger
                title="To"
                subTitle={arrivalAirport?.airportName || 'Select an airport'}
                onClick={() => {
                  setIsAirportSelectDirty(true)
                  openModal(SearchAirports, {
                    tripId,
                    airportType: 'arrival' as const,
                    flightId,
                  })
                }}
              />
              <DatesInputTrigger
                title1="Departing"
                title2="Returning"
                subTitle1="Select date"
                subTitle2="Select date"
                checkinDate={checkinDate}
                checkoutDate={checkoutDate}
                onClick={() => {
                  setIsDatesDirty(true)
                  openModal(SearchDatePicker, {
                    onSetDates,
                    minDate: minSearchDate,
                    maxDate: maxSearchDate,
                    initStartDate: checkinDate,
                    initEndDate: checkoutDate,
                    initVisibleDate:
                      !checkinDate && !checkoutDate ?
                        trip?.startDate :
                        undefined,
                    useSingleDate: fareType?.value === FlightsFareTypes.ONE_WAY,
                    startLabel: fareType?.value === FlightsFareTypes.RETURN ? 'Depart' : undefined,
                    endLabel: fareType?.value === FlightsFareTypes.RETURN ? 'Return' : undefined,
                  })
                }}
                useSingleDate={fareType?.value === FlightsFareTypes.ONE_WAY}
              />
              <GuestsInputTrigger
                occupancies={[occupants]}
                title="Travellers"
                isFlights
                onClick={() =>
                  openModal(SearchOccupancyPicker, {
                    isFlights: true,
                    tripId,
                    initOccupants: [occupants],
                    onSetOccupancies,
                  })
                }
              />
            </VerticalSpacer>
            {isAirportSelectDirty && showSameAirportError && (
              <MessageBanner
                kind="critical"
                description="Please select different departure & arrival airports."
              />
            )}
            {isDatesDirty && showTravelDatesError && (
              <MessageBanner
                kind="critical"
                description="Make sure you select proper dates."
              />
            )}
            {isAirportSelectDirty && showDepartureError && (
              <MessageBanner
                kind="critical"
                description="Please select another departure airport."
              />
            )}
            {isAirportSelectDirty && showDestinationError && (
              <MessageBanner
                kind="critical"
                description="Please select another destination airport."
              />
            )}
            <Group direction="horizontal-reverse">
              <TextButton
                kind="primary"
                fit={isMobile ? 'full-width' : 'auto'}
                onClick={onSearchAirport}
                disabled={!canSearch}
                size="large"
              >
                Search
              </TextButton>
            </Group>
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SearchFlights
